const STORAGE_KEY = 'vision_parked_stream'
const DISMISS_KEY = 'vision_banner_dismissed'

export interface PersistedStream {
  chatId: string
  query: string
}

/** Remember the streaming or aborted chat across a refresh. */
export function syncPersistedStream(
  session: {
    chatId?: string | null
    query?: string
    isStreaming?: boolean
    isAborted?: boolean
  } | null,
): void {
  if (
    session?.chatId &&
    session.query?.trim() &&
    (session.isStreaming || session.isAborted)
  ) {
    writePersistedStream({
      chatId: session.chatId,
      query: session.query,
    })
    return
  }
  clearPersistedStream()
}

export function readPersistedStream(): PersistedStream | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedStream
    if (!parsed?.chatId || !parsed.query?.trim()) return null
    return { chatId: parsed.chatId, query: parsed.query }
  } catch {
    return null
  }
}

export function writePersistedStream(stream: PersistedStream): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stream))
  } catch {
    // ignore quota / private mode
  }
}

export function clearPersistedStream(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore quota / private mode
  }
}

/** Hides every stream banner until Retry or New chat. */
export const STREAM_BANNER_DISMISSED = 'stream:*'

/** Stream-banner dismiss for one chat. Phase (live vs aborted) is not part of it. */
export function streamBannerDismissKey(chatId: string): string {
  return `stream:${chatId}`
}

/**
 * True when X closed the stream banner. A global dismiss stays closed on
 * refresh and on every other history chat.
 */
export function isStreamBannerDismissed(
  dismissed: string | null,
  chatId?: string | null,
): boolean {
  if (!dismissed?.startsWith('stream:')) return false
  if (dismissed === STREAM_BANNER_DISMISSED) return true
  if (!chatId) return false
  const key = streamBannerDismissKey(chatId)
  return dismissed === key || dismissed.startsWith(`${key}:`)
}

/** X on the header banner stays closed across a refresh of this tab. */
export function readDismissedBannerKey(): string | null {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY)
    return raw && raw.trim() ? raw : null
  } catch {
    return null
  }
}

export function writeDismissedBannerKey(key: string | null): void {
  try {
    if (key) sessionStorage.setItem(DISMISS_KEY, key)
    else sessionStorage.removeItem(DISMISS_KEY)
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Chat to reattach after refresh when the open history item is not the
 * one still streaming. Null when this page is already that chat.
 */
export function persistedStreamChatToFollow(
  viewedChatId: string | null | undefined,
  persisted: PersistedStream | null,
): string | null {
  if (!persisted?.chatId || !persisted.query.trim()) return null
  if (viewedChatId && persisted.chatId === viewedChatId) return null
  return persisted.chatId
}
